import React, { useEffect, useState } from 'react';
import { Schedule } from '@classpass/protobuf-definitions/lib/web/models/partners/schedule';
import { parse } from 'date-fns';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import _sortBy from 'lodash/sortBy';
import Isvg from 'react-inlinesvg';

import { createErrorBanner } from 'components/Banner';
import { Card } from 'components/Card';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { PFE_ICONS } from 'constants/icons';
import { intlMessage, rosterPropTypes } from 'constants/propTypes';
import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenueId } from 'selectors/general';

import { selectActiveScheduleId } from '../YieldManagement/YieldManagement.selectors';

import './PrintRoster.scss';

const messages = defineMessages({
  date: {
    id: 'print-roster.date',
    defaultMessage: 'Date',
  },
  time: {
    id: 'print-roster.time',
    defaultMessage: 'Time',
  },
  class: {
    id: 'print-roster.class',
    defaultMessage: 'Class',
  },
  instructor: {
    id: 'print-roster.instructor',
    defaultMessage: 'Staff',
  },
  guestName: {
    id: 'print-roster.guest-name',
    defaultMessage: 'Guest name',
  },
  attended: {
    id: 'print-roster.attended',
    defaultMessage: 'Attended',
  },
  missed: {
    id: 'print-roster.missed',
    defaultMessage: 'Missed',
  },
  note: {
    id: 'print-roster.note',
    defaultMessage: 'Note',
  },
  errorFetchingScheduleData: {
    id: 'print-roster.error-fetching-schedule-data',
    defaultMessage: 'There was a problem fetching schedule data.',
  },
});

const PrintRoster = ({ roster, rosterIsLoading }) => {
  const [schedule, setSchedule] = useState({});
  const [scheduleIsLoading, setScheduleIsLoading] = useState(true);
  const scheduleId = useSelector(selectActiveScheduleId);
  const venueId = useSelector(selectActiveVenueId);
  const dispatch = useDispatch();

  const scheduleName = schedule.scheduleName || schedule.scheduleClass?.className;
  const startDateTime = parse(`${schedule.startDate} ${schedule.startTime}`, 'yyyy-MM-dd HH:mm', new Date());

  useEffect(() => {
    let isMounted = true;

    const fetchSchedule = async () => {
      setScheduleIsLoading(true);
      try {
        const response = await httpClient.get(URLS.venue.schedule(venueId, scheduleId));
        if (isMounted) {
          setSchedule(transformKeysToCamelCase(Schedule.fromJSON(response.data)));
        }
      } catch (error) {
        reportError(error);
        dispatch(createErrorBanner(messages.errorFetchingScheduleData));
      }
      setScheduleIsLoading(false);
    };

    if (scheduleId && venueId) {
      fetchSchedule();
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const listItemData = [
    {
      message: messages.date,
      innerNode: <Message format="long-w-dow" value={startDateTime} />,
    },
    { message: messages.time, innerNode: <Message format="time" value={startDateTime} /> },
    { message: messages.class, innerNode: scheduleName },
    { message: messages.instructor, innerNode: schedule.resource?.resourceName },
  ];
  const renderListItem = ({ message, innerNode }) => (
    <li key={message.id} className="print-roster__info-list-item">
      <strong className="print-roster__info-label">
        <Message message={message} />
      </strong>
      {scheduleIsLoading ? <LoadingDots /> : innerNode}
    </li>
  );

  renderListItem.propTypes = {
    message: intlMessage,
    innerNode: PropTypes.node,
  };

  const thData = [
    { classes: 'print-roster__name-cell', message: messages.guestName },
    { classes: '', message: messages.attended },
    { classes: '', message: messages.missed },
    { classes: 'print-roster__note-cell', message: messages.note },
  ];
  const renderTh = ({ classes, message }) => (
    <th key={message.id} className={`print-roster__cell print-roster__header-cell ${classes}`}>
      <Message message={message} />
    </th>
  );

  renderTh.propTypes = {
    classes: PropTypes.string,
    message: intlMessage,
  };

  return (
    <Card>
      <Isvg className="print-roster__logo" src={PFE_ICONS.logotype} />
      <ul>{listItemData.map(renderListItem)}</ul>

      <table className="print-roster__table">
        <thead>
          <tr>{thData.map(renderTh)}</tr>
        </thead>
        <tbody>
          {!rosterIsLoading &&
            _sortBy(roster, 'firstName').map(({ firstName, lastName, reservationId }) => (
              <tr key={reservationId}>
                <td className="print-roster__cell print-roster__name-cell">
                  {firstName} {lastName}
                </td>
                <td className="print-roster__cell" />
                <td className="print-roster__cell" />
                <td className="print-roster__cell print-roster__note-cell" />
              </tr>
            ))}
        </tbody>
      </table>
      {rosterIsLoading && <LoadingDots />}
    </Card>
  );
};

PrintRoster.propTypes = {
  roster: rosterPropTypes,
  rosterIsLoading: PropTypes.bool,
};

export default PrintRoster;
